/**
 * Runtime API Configuration
 * Ermittelt die Backend-URL basierend auf dem aktuellen Host
 */

export function getApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    // Laufzeit-Injektion pro Stage (siehe deployment/): der Container schreibt
    // window.__API_BASE_URL__, bevor das Bundle lädt. Deshalb optional und string.
    const runtimeUrl = (window as Window & { __API_BASE_URL__?: string }).__API_BASE_URL__;
    if (runtimeUrl) return runtimeUrl;

    const { hostname, protocol, port, origin } = window.location;

    // Dev: Vite läuft typischerweise auf :5173 (oder 5174/5175), Backend auf :3000
    if (port === '5173' || port === '5174' || port === '5175') {
      return `${protocol}//${hostname}:3000`;
    }

    // Production/Docker: gleiche Origin nutzen (NGINX proxied /api → Backend)
    return origin;
  }

  // Kein Browser (Node-Testlauf ohne jsdom): das CMS ist eine reine SPA, hier läuft im Betrieb
  // nichts. Ein `import.meta.env.VITE_API_BASE_URL` stand hier und ist mit A-02 gefallen — Vite
  // hätte den Wert beim Bauen eingesetzt und damit die Stufe ins Bündel gebacken, für einen
  // Zweig, den kein Betrieb je erreicht.
  return 'http://localhost:3000';
}

/**
 * Ein Wert aus der Laufzeit-Konfiguration, die der Container beim Start schreibt.
 *
 * Quelle ist `/api-config.runtime.js`, erzeugt von
 * `docker/40-laufzeit-konfig.sh` aus der Container-Umgebung; im lokalen `vite dev` liefert die
 * mitgelieferte Fassung unter `public/` dieselben Globalen mit leeren Werten.
 *
 * ⚠️ Leer heißt „nicht gesetzt" und wird als leerer String zurückgegeben, nicht als `undefined`:
 * die Aufrufer prüfen auf Wahrheitswert, und ein `undefined` in einer Fehlermeldung liest sich wie
 * ein Programmfehler statt wie eine fehlende Umgebungsvariable.
 */
export function laufzeitWert(schluessel: '__SUPABASE_URL__' | '__SUPABASE_ANON_KEY__'): string {
  if (typeof window !== 'undefined') {
    const wert = (window as unknown as Record<string, unknown>)[schluessel];
    if (typeof wert === 'string' && wert !== '') return wert;
  }

  // ── Rückfall NUR im lokalen `vite dev` ────────────────────────────────────────────────────
  // Lokal gibt es keinen Container-Start, der das Konfig-Skript erzeugt — die Datei unter
  // `public/` liefert leere Werte. Ohne diesen Zweig könnte niemand das CMS mehr lokal
  // anmelden, und das wäre ein Preis, den A-02 nicht verlangt.
  //
  // ⛔ `import.meta.env.DEV` ist die ganze Sicherheit dieser Zeilen, und sie ist gemessen, nicht
  // geglaubt: Vite ersetzt `DEV` im Produktionsbau durch `false` und wirft den Zweig samt der
  // `VITE_*`-Werte weg. Belegt von `scripts/a02-buendel-check.mjs`, das nach `vite build` im
  // erzeugten `dist/` nach den Bauzeit-Werten sucht — und im Docker-Bau direkt hinter dem Build
  // läuft. Wer den `DEV`-Riegel entfernt, backt die Stufe wieder ein; das Tor sagt es ihm.
  if (import.meta.env.DEV) {
    const ausEnv =
      schluessel === '__SUPABASE_URL__'
        ? import.meta.env.VITE_SUPABASE_URL
        : import.meta.env.VITE_SUPABASE_ANON_KEY;
    return typeof ausEnv === 'string' ? ausEnv : '';
  }

  return '';
}

/**
 * Welche Laufzeit-Variablen fehlen — leer heißt „alles da".
 *
 * Gibt die Namen zurück, unter denen man sie SETZT (`SPCORE_*`), nicht die Fenster-Schlüssel:
 * wer diese Meldung liest, muss wissen, was er in die Compose schreiben soll, nicht wie das
 * Skript intern heißt.
 *
 * ⚠️ Die API-Basis steht bewusst NICHT in dieser Liste. Fehlt sie, leitet das erzeugte Skript
 * sie aus `window.location` her — das ist ein gültiger Betriebsfall (gleicher Ursprung hinter
 * nginx) und kein Mangel. Die zwei Supabase-Werte haben keine solche Herleitung: ohne sie gibt es
 * keine Anmeldung, und ohne Anmeldung kein CMS.
 */
export function fehlendeKonfig(): string[] {
  const fehlend: string[] = [];
  if (!laufzeitWert('__SUPABASE_URL__')) fehlend.push('SPCORE_SUPABASE_URL');
  if (!laufzeitWert('__SUPABASE_ANON_KEY__')) fehlend.push('SPCORE_SUPABASE_ANON_KEY');
  return fehlend;
}
